import Select from 'react-select';
import { StateManagerProps } from 'react-select/dist/declarations/src/useStateManager';
import { useCallback } from 'react';
import { InputProps } from 'react-select/dist/declarations/src/components/Input';
import clsx from 'clsx';

import { Input } from 'components/Input/Input';
import { SearchIcon } from 'components/Icons/SearchIcon';

export interface AutoCompleteInputOption {
  value: string;
  label: string;
  subLabel: string;
  index: number;
}

interface AutoCompleteInputProps extends StateManagerProps<AutoCompleteInputOption> {
  id: string;
  name: string;
  label: string;
  placeholder?: string;
  helperText?: string;
  errorText?: string;
}

export const AutoCompleteInput = ({
  name,
  label,
  id,
  helperText,
  errorText,
  placeholder,
  ...props
}: AutoCompleteInputProps) => {
  const CustomInput = useCallback(
    ({
      // The following unused props are spread because they are required by the Select component but not used directly by the CustomInput component
      /* eslint-disable @typescript-eslint/no-unused-vars,unused-imports/no-unused-vars */
      clearValue,
      getStyles,
      cx,
      isHidden,
      selectProps,
      selectOption,
      isMulti,
      hasValue,
      getValue,
      setValue,
      isRtl,
      getClassNames,
      options,
      theme,
      /* eslint-enable  */
      isDisabled,
      innerRef,
      ...props
    }: InputProps<AutoCompleteInputOption>) => {
      return (
        <div className="relative">
          <Input
            ref={innerRef}
            label={label}
            id={id}
            placeholder={placeholder}
            name={name}
            helperText={helperText}
            errorText={errorText}
            className="mx-0.5 [&>input]:w-full [&>input]:!pl-9"
            disabled={isDisabled}
            {...props}
          />
          <SearchIcon className="absolute left-3 top-[46%] text-secondaryLight" />
        </div>
      );
    },
    [errorText, helperText, id, label, name, placeholder]
  );

  return (
    <Select
      unstyled
      noOptionsMessage={() => null}
      classNames={{
        menuList: () => 'border border-graySecondary bg-white mt-[-18px]',
        menu: () => '!z-20'
      }}
      components={{
        DropdownIndicator: undefined,
        NoOptionsMessage: () => undefined,
        SingleValue: () => undefined,
        Placeholder: () => undefined,
        Input: CustomInput,
        Option: ({ isFocused, data: { label, subLabel, index }, innerRef, innerProps }) => (
          <div
            data-testid={`${id}-option-${index}`}
            className={clsx(
              'flex flex-col p-2 hover:bg-lightSecondary focus:bg-lightSecondary active:bg-lightPrimary',
              { 'bg-lightSecondary': isFocused }
            )}
            role="button"
            tabIndex={-1}
            {...innerProps}
            ref={innerRef}
          >
            <span className="text-sm text-grayPrimary">{label}</span>
            <span className="text-xs text-grayPrimary">{subLabel}</span>
          </div>
        )
      }}
      {...props}
    />
  );
};
